import { Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import {
  Field,
  FieldArray,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";
import { ConfigItem as Item } from "../../../app/backendTypes";
import UserPicker from "../users/UserPicker";
import { OrderAdornment } from "./OrderAdornment";

export function ItemApprovals(props: { loading: boolean }) {
  const { values, getFieldMeta, handleChange, setFieldTouched, setFieldValue } =
    useFormikContext<Item>();

  if (values.approvals === undefined) return <></>;

  const loading = props.loading;

  const meta: {
    kind: FieldMetaProps<string>;
    inner: FieldMetaProps<string>;
  }[] = [];

  for (let i = 0; i < values.approvals.steps.length; i++) {
    const metaStep = `approvals.steps[${i}]`;
    meta.push({
      kind: getFieldMeta(`${metaStep}.kind`),
      inner: getFieldMeta(`${metaStep}.inner.id`),
    });
  }

  return (
    <FieldArray
      name={"approvals.steps"}
      render={(arrayHelpers) => (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography color="text.secondary">
              Steps are ordered top to bottom
            </Typography>
            <Button
              onClick={() => arrayHelpers.push({ kind: "", inner: { id: 0 } })}
              disabled={loading}
              color="secondary"
            >
              Add Step
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {values.approvals?.steps.map((step, i) => (
              <Box
                key={i}
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <OrderAdornment index={i} arrayHelpers={arrayHelpers} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 2,
                    width: "100%",
                    alignItems: "baseline",
                  }}
                >
                  <Box flexGrow={1} width={{ xs: "100%", sm: null }}>
                    <Field name={`approvals.steps[${i}].kind`}>
                      {({ field }: { field: FieldInputProps<unknown> }) => (
                        <FormControl fullWidth>
                          <Select
                            size="small"
                            error={
                              meta[i].kind.touched &&
                              Boolean(meta[i].kind.error)
                            }
                            disabled={loading}
                            displayEmpty
                            renderValue={(val) =>
                              val === "" ? (
                                <Typography
                                  color={
                                    meta[i].kind.touched &&
                                    Boolean(meta[i].kind.error)
                                      ? "error"
                                      : "text.secondary"
                                  }
                                >
                                  Kind
                                </Typography>
                              ) : (
                                (val as string)
                              )
                            }
                            {...field}
                            onChange={(e: SelectChangeEvent<unknown>) => {
                              if (values.approvals !== undefined)
                                if (e.target.value === "User")
                                  if (
                                    values.approvals.steps[i].inner ===
                                    undefined
                                  )
                                    values.approvals.steps[i].inner = { id: 0 };

                              setFieldTouched(
                                `approvals.steps[${i}].inner.id`,
                                false
                              );

                              handleChange(e);
                            }}
                          >
                            <MenuItem value="Manager">Manager</MenuItem>
                            <MenuItem value="Dynamic">Dynamic</MenuItem>
                            <MenuItem value="User">User</MenuItem>
                          </Select>
                          {meta[i].kind.touched &&
                            Boolean(meta[i].kind.error) && (
                              <FormHelperText error>
                                {meta[i].kind.error}
                              </FormHelperText>
                            )}
                        </FormControl>
                      )}
                    </Field>
                  </Box>
                  {meta[i].kind.value === "User" && (
                    <Box flexGrow={2} width={{ xs: "100%", sm: null }}>
                      <Field name={`approvals.steps[${i}].inner.id`}>
                        {({ field }: { field: FieldInputProps<number> }) => (
                          <UserPicker
                            initialValue={field.value}
                            onBlur={() =>
                              setFieldTouched(
                                `approvals.steps[${i}].inner.id`,
                                true
                              )
                            }
                            onChange={(value) =>
                              setFieldValue(
                                `approvals.steps[${i}].inner.id`,
                                value !== null ? value.employee.id : 0
                              )
                            }
                            meta={meta[i].inner}
                            disabled={loading}
                            small
                          />
                        )}
                      </Field>
                    </Box>
                  )}
                </Box>
                <IconButton
                  onClick={() => arrayHelpers.remove(i)}
                  disabled={loading}
                >
                  <Delete color={loading ? "disabled" : "error"} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </>
      )}
    />
  );
}
